// Graficul: Lightweight Charts + panouri de indicatori sincronizate + straturi proprii.
//
// Lightweight Charts v4 nu are panouri multiple, așa că un „grafic" e de fapt un teanc de
// instanțe LWC cu scara de timp și crosshair-ul sincronizate. Panoul principal ține
// lumânările, volumul și suprapunerile (SMA/EMA/BB, niveluri, desene); RSI, MACD, AO,
// CVD și Open Interest primesc fiecare panoul lui, sub el.

import { FutureAxis } from './future-axis.js';
import { client } from '../core/exchange-client.js';
import { bus, el, clear, TF_SECONDS, priceDigits, fmtPrice } from '../core/util.js';
import { chartTheme, withAlpha } from '../core/theme.js';
import * as ind from './indicators.js';
import { QuickMeasure } from './measure.js';
import { LevelLayer, applyLevelTheme } from './levels.js';

const LWC = () => window.LightweightCharts;

/**
 * Paleta activă. Nu mai e o constantă: temele o rescriu, iar graficele deschise se
 * recolorează pe loc. Rămâne exportată ca obiect *mutat*, nu înlocuit, ca importurile
 * existente să vadă mereu valorile curente.
 */
export const THEME = { ...chartTheme() };

/**
 * Treptele de pe scara volumului: numere rotunde din scara 1-2-5, nu fracțiuni ale
 * maximului. Un „24.7M" scris pe axă e ilizibil dintr-o privire; „20M" nu.
 */
function trepteVolum(maxim) {
  const brut = maxim / 3;
  const putere = 10 ** Math.floor(Math.log10(brut));
  const n = brut / putere;
  const pas = (n >= 5 ? 5 : n >= 2 ? 2 : 1) * putere;
  const trepte = [];
  for (let v = pas; v < maxim * 0.96 && trepte.length < 4; v += pas) trepte.push(v);
  return trepte;
}

/** 25000000 -> „25M". Treptele fiind rotunde, zecimala e aproape mereu zero. */
function etichetaVolum(v) {
  const [prag, sufix] = v >= 1e9 ? [1e9, 'B'] : v >= 1e6 ? [1e6, 'M'] : v >= 1e3 ? [1e3, 'K'] : [1, ''];
  const n = v / prag;
  return `${n >= 100 || Number.isInteger(n) ? Math.round(n) : n.toFixed(1)}${sufix}`;
}

function refreshTheme() {
  Object.assign(THEME, chartTheme());
  return THEME;
}

// Citim tema la fiecare grafic nou: modulul ăsta se încarcă înaintea celui care
// aplică tema salvată, deci valorile prinse la import ar fi cele implicite.
const BASE_OPTIONS = () => (refreshTheme(), {
  layout: {
    background: { type: 'solid', color: THEME.bg },
    textColor: THEME.text,
    fontSize: 11,
    fontFamily: "'Inter', -apple-system, 'Segoe UI', sans-serif",
    attributionLogo: false,
  },
  grid: {
    vertLines: { color: THEME.grid },
    horzLines: { color: THEME.grid },
  },
  crosshair: {
    mode: 0,
    vertLine: { color: THEME.crosshair, width: 1, style: 3, labelBackgroundColor: THEME.border },
    horzLine: { color: THEME.crosshair, width: 1, style: 3, labelBackgroundColor: THEME.border },
  },
  rightPriceScale: { borderColor: THEME.border, scaleMargins: { top: 0.08, bottom: 0.25 } },
  timeScale: {
    borderColor: THEME.border,
    timeVisible: true,
    secondsVisible: false,
    rightOffset: 4,
    barSpacing: 6,
  },
  handleScroll: { mouseWheel: true, pressedMouseMove: true },
  handleScale: { mouseWheel: true, pinch: true, axisPressedMouseMove: true },
});

/** Panourile disponibile sub grafic, în ordinea de afișare. */
const PANE_DEFS = [
  { id: 'oichart', label: 'Открытый интерес', height: 90 },
  { id: 'rsi', label: 'RSI (14)', height: 80 },
  { id: 'macd', label: 'MACD (12,26,9)', height: 80 },
  { id: 'ao', label: 'Awesome Oscillator', height: 80 },
  { id: 'cvd', label: 'CVD', height: 80 },
];

export class Chart {
  /**
   * @param {HTMLElement} container
   * @param {object} opts { exchange, market, symbol, tf, indicators, compact, showLevels }
   */
  constructor(container, opts = {}) {
    this.container = container;
    this.exchange = opts.exchange || 'binance';
    this.market = opts.market || 'futures';
    this.symbol = opts.symbol || 'BTCUSDT';
    this.tf = opts.tf || '5m';
    this.indicators = { volume: true, ...(opts.indicators || {}) };
    this.compact = !!opts.compact;
    this.showLevels = opts.showLevels !== false;
    this.limit = opts.limit || (this.compact ? 300 : 1000);

    this.bars = [];
    this.panes = new Map();
    this.overlays = new Map();
    this.priceLines = [];
    this.unsubs = [];
    this.destroyed = false;
    this.onBar = opts.onBar || null;
    this.cvdRunning = 0;

    this.#build();
  }

  // -------------------------------------------------------------- construcție

  #build() {
    this.root = el('div', { class: 'chart-root' });
    this.mainEl = el('div', { class: 'chart-pane chart-pane-main' });
    this.panesEl = el('div', { class: 'chart-panes' });
    this.root.append(this.mainEl, this.panesEl);
    this.container.append(this.root);

    const options = BASE_OPTIONS();
    if (this.compact) {
      options.timeScale.barSpacing = 4;
      options.layout.fontSize = 10;
    }
    this.chart = LWC().createChart(this.mainEl, options);
    // Orele și în zona goală din dreapta (biblioteca le pune doar sub barele existente).
    if (!this.compact) this.addLayer(new FutureAxis(this, THEME));

    this.candles = this.chart.addCandlestickSeries({
      upColor: THEME.up, downColor: THEME.down,
      borderUpColor: THEME.up, borderDownColor: THEME.down,
      wickUpColor: THEME.upWick, wickDownColor: THEME.downWick,
      priceLineColor: THEME.down,
      priceLineWidth: 1,
      priceLineStyle: 2,
    });
    this.priceLineTone = 'down';

    this.volume = this.chart.addHistogramSeries({
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
      priceLineVisible: false,
      lastValueVisible: false,
    });
    // Aceleași proporții ca la ScalpX: volumul ocupă banda de jos de 15%,
    // lumânările stau între 8% de sus și 22% de jos, ca să nu se suprapună.
    this.chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.85, bottom: 0 },
      visible: false,
    });
    this.#applyCandleMargins();

    // Eticheta „Объём", ca la ei, în colțul de jos-stânga al zonei de volum.
    this.volumeLabel = el('span', { class: 'chart-vol-label', text: 'Объём' });
    this.mainEl.append(this.volumeLabel);

    // Cifrele de volum din jgheabul din dreapta. Lightweight Charts nu randează scările
    // suprapuse — `priceScale('volume').width()` rămâne 0 oricât ai cere `visible: true`
    // —, deci le desenăm noi. Poziția nu e ghicită: fiecare etichetă stă exact acolo
    // unde seria însăși pune valoarea, prin `priceToCoordinate`.
    this.volScaleEl = el('div', { class: 'chart-vol-scale' });
    this.mainEl.append(this.volScaleEl);

    // Nivelurile iau paleta temei încă de la creare, nu doar la o schimbare ulterioară:
    // altfel primul grafic deschis le-ar desena în culorile temei implicite.
    applyLevelTheme(THEME);
    this.levelLayer = new LevelLayer(this, this.mainEl);
    // Măsurarea cu butonul din mijloc merge pe orice grafic, inclusiv pe cele din grilă.
    this.measure = new QuickMeasure(this, this.mainEl);

    this.#observeResize();
    this.#watchView();
    this.#syncPanes();

    // Tema se poate schimba cât graficul e deschis — inclusiv pe zecile de carduri
    // din grilă, care trebuie să se recoloreze toate, nu doar cel din față.
    this.offTheme = bus.on('theme:change', () => {
      if (!this.destroyed) this.applyTheme();
    });
  }

  /**
   * Precizia axei de preț, dedusă din mărimea prețului.
   *
   * Fără asta, Lightweight Charts folosește două zecimale pentru orice: o monedă la
   * 0.0243 apărea de mai multe ori ca „0.02" pe axă, iar nivelurile deveneau
   * indistinguibile. Alegem numărul de zecimale după preț și derivăm pasul minim.
   */
  #applyPricePrecision(bars) {
    if (!bars.length) return;
    const last = bars[bars.length - 1].close;
    const precision = priceDigits(last);
    if (precision === this.pricePrecision) return;
    this.pricePrecision = precision;
    this.candles.applyOptions({
      priceFormat: { type: 'price', precision, minMove: 10 ** -precision },
    });
  }

  /** Eticheta ultimului preț se colorează după direcția lumânării, ca la ScalpX. */
  #tintPriceLine(bar) {
    const tone = bar.close >= bar.open ? 'up' : 'down';
    if (tone === this.priceLineTone) return;
    this.priceLineTone = tone;
    this.candles.applyOptions({ priceLineColor: tone === 'up' ? THEME.up : THEME.down });
  }

  /**
   * Recolorează graficul deschis când se schimbă tema sau culorile lumânărilor.
   *
   * Se aplică pe fiecare panou, nu doar pe cel principal: RSI, MACD și celelalte au
   * instanțe LWC proprii, iar una uitată ar rămâne cu fundalul temei vechi — o dungă
   * de altă culoare sub grafic.
   */
  applyTheme() {
    refreshTheme();
    const base = BASE_OPTIONS();
    const shared = {
      layout: base.layout,
      grid: base.grid,
      crosshair: base.crosshair,
      rightPriceScale: { borderColor: THEME.border },
      timeScale: { borderColor: THEME.border },
    };
    this.chart.applyOptions(shared);
    for (const pane of this.panes?.values?.() || []) pane.chart?.applyOptions(shared);

    this.candles.applyOptions({
      upColor: THEME.up, downColor: THEME.down,
      borderUpColor: THEME.up, borderDownColor: THEME.down,
      wickUpColor: THEME.upWick, wickDownColor: THEME.downWick,
      priceLineColor: this.priceLineTone === 'up' ? THEME.up : THEME.down,
    });
    if (this.indicators.volume) this.volume.setData(ind.volumeSeries(this.bars));
    applyLevelTheme(THEME);
    this.levelLayer?.render?.();
  }

  #applyCandleMargins() {
    this.chart.priceScale('right').applyOptions({
      scaleMargins: { top: 0.08, bottom: this.indicators.volume ? 0.22 : 0.06 },
    });
    if (this.volumeLabel) {
      this.volumeLabel.style.display = this.indicators.volume ? '' : 'none';
    }
  }

  #observeResize() {
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.container);
  }

  /**
   * Anunță straturile proprii când proiecția s-a schimbat.
   *
   * Lightweight Charts oferă un eveniment doar pentru scara de timp. Scara de preț se
   * mișcă însă și la tragerea axei, și la fiecare reajustare automată — iar atunci
   * nivelurile și desenele rămâneau la coordonatele vechi și arătau prețuri greșite.
   * Comparăm proiecția la fiecare cadru și redesenăm doar când chiar s-a schimbat.
   */
  #watchView() {
    const projection = () => {
      const range = this.chart.timeScale().getVisibleLogicalRange();
      const height = this.mainEl.clientHeight;
      // Prețurile din colțurile panoului surprind orice deplasare sau scalare verticală.
      const top = this.candles.coordinateToPrice(0);
      const bottom = this.candles.coordinateToPrice(height);
      return range ? `${range.from.toFixed(2)}|${range.to.toFixed(2)}|${top}|${bottom}` : '';
    };

    let last = '';
    const tick = () => {
      if (this.destroyed) return;
      const now = projection();
      if (now && now !== last) {
        last = now;
        this.levelLayer?.render();
        this.drawLayer?.render();
        this.measure?.render();
        for (const layer of this.extraLayers || []) layer.render();
      }
      this.#renderVolumeScale();
      this.viewFrame = requestAnimationFrame(tick);
    };
    this.viewFrame = requestAnimationFrame(tick);
  }

  /** Straturile care trebuie redesenate când se mișcă graficul se înregistrează aici. */
  attachDrawLayer(layer) {
    this.drawLayer = layer;
  }

  /**
   * Un strat în plus (de ex. „Крупный игрок"): e redesenat la fiecare mișcare a
   * graficului și distrus odată cu el. Trebuie să aibă `render()` și `destroy()`.
   */
  addLayer(layer) {
    (this.extraLayers ||= []).push(layer);
    return layer;
  }

  resize() {
    if (this.destroyed) return;
    const width = this.container.clientWidth;
    const total = this.container.clientHeight;
    const panes = [...this.panes.values()];
    const wanted = panes.reduce((a, p) => a + p.height, 0);

    // Lumânările rămân eroul: panourile nu pot lua mai mult de 55% din înălțime.
    // Cu multe panouri active se comprimă proporțional, în loc să strivească graficul.
    const budget = Math.min(wanted, total * 0.55);
    const scale = wanted > 0 ? budget / wanted : 1;

    this.chart.resize(width, Math.max(80, total - budget));
    for (const pane of panes) {
      const h = Math.max(48, Math.round(pane.height * scale));
      pane.element.style.height = `${h}px`;
      pane.chart.resize(width, h);
    }
  }

  // ------------------------------------------------------------------ date

  async load() {
    const bars = await client.klines(this.exchange, this.market, this.symbol, this.tf, this.limit);
    if (this.destroyed) return;
    this.bars = bars;
    this.#applyPricePrecision(bars);
    this.candles.setData(bars);
    if (bars.length) this.#tintPriceLine(bars[bars.length - 1]);
    this.#renderVolume();
    this.#renderOverlays();
    this.#renderPanes();
    this.#renderLevels();
    this.resetView();
    this.#subscribeLive();
    this.#watchHistory();
    bus.emit('chart:loaded', { symbol: this.symbol, tf: this.tf, bars: bars.length });
    return bars;
  }

  /**
   * Alimentează graficul din afară, fără să ceară nimic bursei.
   *
   * Simulatorul de replay ştie el ce bare există la momentul „acum" al reluării —
   * graficul nu trebuie să le caute singur, şi mai ales nu trebuie să se aboneze la
   * preţul live: într-o reluare din 2024, o lumânare de azi ar fi o scurgere din viitor.
   */
  setBars(bars) {
    this.#clearSubs();
    this.bars = bars.slice();
    this.#applyPricePrecision(this.bars);
    this.candles.setData(this.bars);
    if (this.bars.length) this.#tintPriceLine(this.bars[this.bars.length - 1]);
    this.#renderVolume();
    this.#renderOverlays();
    this.#renderPanes();
    this.#renderLevels();
    return this.bars;
  }

  /** O bară nouă la capătul reluării. Actualizează seriile fără să redeseneze tot. */
  pushBar(bar) {
    const ultima = this.bars[this.bars.length - 1];
    if (ultima && bar.time === ultima.time) this.bars[this.bars.length - 1] = bar;
    else this.bars.push(bar);
    this.candles.update(bar);
    if (this.indicators.volume) {
      this.volume.update({
        time: bar.time,
        value: bar.volume || 0,
        color: bar.close >= bar.open ? withAlpha(THEME.up, .5) : withAlpha(THEME.down, .5),
      });
    }
    this.#tintPriceLine(bar);
    // Panourile şi suprapunerile lucrează pe toată seria; le recalculăm mai rar decât
    // lumânările, altfel la 10× viteză s-ar reface RSI-ul de zece ori pe secundă.
    if (this.bars.length % 5 === 0) { this.#renderOverlays(); this.#renderPanes({ live: true }); }
  }

  #subscribeLive() {
    this.#clearSubs();
    const step = TF_SECONDS[this.tf] || 300;

    // Graficul mare e cel privit: primește ritmul întreg chiar dacă în spate mai stau
    // zeci de carduri care își împart restul bugetului de cereri.
    this.unsubs.push(client.subscribe(
      this.exchange, this.market, this.symbol, 'kline',
      ({ bar }) => this.#applyBar(bar, step),
      this.tf,
      { priority: !this.compact },
    ));

    // CVD real are nevoie de fluxul de tranzacții, nu de aproximarea din lumânări.
    if (this.indicators.cvd) {
      this.unsubs.push(client.subscribe(
        this.exchange, this.market, this.symbol, 'trade',
        (trade) => this.#applyTrade(trade, step),
      ));
    }
  }

  #clearSubs() {
    this.unsubs.forEach((fn) => fn());
    this.unsubs = [];
  }

  /**
   * Încărcare leneșă a istoricului: când derulezi spre stânga și te apropii de prima
   * lumânare, aducem încă un bloc mai vechi și îl prependăm. Așa graficul „se citește
   * în lungime" oricât de departe mergi, ca la ScalpX.
   */
  #watchHistory() {
    this.historyExhausted = false;
    this.loadingHistory = false;
    this.historyUnsub?.();

    this.historyUnsub = this.chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (!range || this.loadingHistory || this.historyExhausted || this.destroyed) return;
      // Mai puțin de 30 de bare până la marginea din stânga a datelor încărcate.
      if (range.from > 30) return;
      this.#loadOlder();
    });
  }

  async #loadOlder() {
    if (this.loadingHistory || this.historyExhausted || !this.bars.length) return;
    this.loadingHistory = true;
    const oldestMs = this.bars[0].time * 1000;

    try {
      const older = await client.klines(
        this.exchange, this.market, this.symbol, this.tf, this.limit, oldestMs - 1,
      );
      if (this.destroyed) return;

      const fresh = older.filter((b) => b.time < this.bars[0].time);
      if (!fresh.length) { this.historyExhausted = true; return; }

      // Prependarea deplasează indicii logici: compensăm ca vederea să nu sară.
      const range = this.chart.timeScale().getVisibleLogicalRange();
      this.bars = [...fresh, ...this.bars];
      this.candles.setData(this.bars);
      if (this.indicators.volume) this.volume.setData(ind.volumeSeries(this.bars));
      this.#renderOverlays();
      this.#renderPanes();
      if (range) {
        this.chart.timeScale().setVisibleLogicalRange({
          from: range.from + fresh.length,
          to: range.to + fresh.length,
        });
      }
    } catch (err) {
      console.warn(`[history] ${this.symbol}: ${err.message}`);
      this.historyExhausted = true;
    } finally {
      this.loadingHistory = false;
    }
  }

  #applyBar(bar, step) {
    if (this.destroyed || !bar) return;
    const aligned = { ...bar, time: Math.floor(bar.time / step) * step };
    const last = this.bars[this.bars.length - 1];

    // O bară mai veche decât ultima e o corecție a uneia deja desenate (interogarea de
    // rezervă trimite câteva bare deodată). O rescriem doar în memorie: `update()`
    // acceptă numai ultima bară și ar arunca „Cannot update oldest data", iar excepția
    // ar opri și actualizarea barelor următoare — adică ar îngheța graficul.
    if (last && aligned.time < last.time) {
      const i = this.bars.findIndex((b) => b.time === aligned.time);
      if (i >= 0) this.bars[i] = aligned;
      return;
    }

    if (last && aligned.time === last.time) this.bars[this.bars.length - 1] = aligned;
    else this.bars.push(aligned);

    try {
      this.candles.update(aligned);
    } catch (err) {
      // Plasă de siguranță: o singură bară respinsă nu trebuie să oprească graficul.
      console.warn(`[chart] ${this.symbol}: ${err?.message || err}`);
      return;
    }
    this.#tintPriceLine(aligned);
    if (this.indicators.volume) {
      this.volume.update({
        time: aligned.time,
        value: aligned.volume,
        color: withAlpha(aligned.close >= aligned.open ? THEME.up : THEME.down, 0.55),
      });
    }
    this.#renderOverlays();
    this.#renderPanes({ live: true });
    this.onBar?.(aligned, this.bars);
    bus.emit('chart:bar', { symbol: this.symbol, bar: aligned });
  }

  #applyTrade(trade, step) {
    const pane = this.panes.get('cvd');
    if (!pane) return;
    this.cvdRunning += trade.buyerMaker ? -trade.qty * trade.price : trade.qty * trade.price;
    pane.series.line.update({
      time: Math.floor(trade.time / step) * step,
      value: this.cvdRunning,
    });
  }

  // ------------------------------------------------------------- randare

  #renderVolume() {
    this.#applyCandleMargins();
    if (!this.indicators.volume) {
      this.volume.setData([]);
      return;
    }
    this.volume.setData(ind.volumeSeries(this.bars));
  }

  /**
   * Cifrele de pe scara volumului.
   *
   * Rulează în bucla de cadre a lui `#watchView`, deci trebuie să fie ieftină: își
   * calculează o semnătură (maximul vizibil + geometria) și atinge DOM-ul doar când
   * aceasta chiar s-a schimbat. Pe cardurile compacte nu are ce căuta — acolo graficul
   * e de mărimea unei unghii și cifrele ar fi doar zgomot.
   */
  #renderVolumeScale() {
    const gazda = this.volScaleEl;
    if (!gazda) return;
    if (!this.indicators.volume || this.compact) {
      if (gazda.childElementCount) clear(gazda);
      return;
    }

    const date = this.volume.data();
    const interval = this.chart.timeScale().getVisibleLogicalRange();
    if (!date.length || !interval) { clear(gazda); return; }

    // Seria se auto-scalează după ce se vede, nu după toată istoria: scara trebuie să
    // urmeze exact ce e pe ecran, altfel cifrele mint la orice zoom.
    const de_la = Math.max(0, Math.floor(interval.from));
    const pana = Math.min(date.length, Math.ceil(interval.to) + 1);
    let maxim = 0;
    for (let i = de_la; i < pana; i++) if (date[i].value > maxim) maxim = date[i].value;
    if (maxim <= 0) { clear(gazda); return; }

    const latimeAxa = this.chart.priceScale('right').width();
    const semnatura = `${maxim}|${this.mainEl.clientHeight}|${latimeAxa}`;
    if (semnatura === this.volScaleSig) return;
    this.volScaleSig = semnatura;

    clear(gazda);
    gazda.style.width = `${latimeAxa}px`;
    // Axa de preţ îşi desenează etichetele pe toată înălţimea, inclusiv peste banda
    // volumului — acolo două rânduri de cifre se suprapuneau şi nu se citea niciunul.
    // Banda primeşte fundalul graficului: în dreptul volumului rămân doar cifrele lui.
    const susBanda = (this.chart.priceScale('volume').options().scaleMargins.top || 0.85)
      * this.mainEl.clientHeight;
    gazda.style.top = `${susBanda}px`;

    for (const valoare of trepteVolum(maxim)) {
      const y = this.volume.priceToCoordinate(valoare);
      if (y == null) continue;
      gazda.append(el('span', {
        class: 'chart-vol-tick',
        text: etichetaVolum(valoare),
        style: `top:${y - susBanda}px`,   // coordonatele sunt ale panoului, banda începe mai jos
      }));
    }
  }

  /** Suprapunerile de pe panoul principal: SMA, EMA, Bollinger. */
  #renderOverlays() {
    const want = new Set();
    if (this.indicators.sma) want.add('sma');
    if (this.indicators.ema) want.add('ema');
    if (this.indicators.bb) want.add('bb');

    for (const [id, series] of this.overlays) {
      if (!want.has(id)) {
        [].concat(series).forEach((s) => this.chart.removeSeries(s));
        this.overlays.delete(id);
      }
    }
    if (!this.bars.length) return;

    if (want.has('sma')) {
      let s = this.overlays.get('sma');
      if (!s) {
        s = this.chart.addLineSeries({
          color: '#f0b90b', lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
        });
        this.overlays.set('sma', s);
      }
      s.setData(ind.sma(this.bars, 20));
    }

    if (want.has('ema')) {
      let s = this.overlays.get('ema');
      if (!s) {
        s = this.chart.addLineSeries({
          color: '#2f81f7', lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
        });
        this.overlays.set('ema', s);
      }
      s.setData(ind.ema(this.bars, 21));
    }

    if (want.has('bb')) {
      let trio = this.overlays.get('bb');
      if (!trio) {
        const mk = (color, width) => this.chart.addLineSeries({
          color, lineWidth: width, priceLineVisible: false, lastValueVisible: false,
        });
        trio = [mk('rgba(150,160,190,0.6)', 1), mk('rgba(150,160,190,0.35)', 1), mk('rgba(150,160,190,0.6)', 1)];
        this.overlays.set('bb', trio);
      }
      const { upper, middle, lower } = ind.bollinger(this.bars, 20, 2);
      trio[0].setData(upper);
      trio[1].setData(middle);
      trio[2].setData(lower);
    }
  }

  /** Niveluri automate de suport/rezistență, desenate de `LevelLayer`. */
  #renderLevels() {
    if (!this.levelLayer) return;
    if (!this.showLevels || this.bars.length < 30) { this.levelLayer.setLevels([]); return; }
    this.levelLayer.setLevels(ind.autoLevels(this.bars, {
      maxLevels: this.compact ? 5 : 8,
    }));
  }

  setLevelsVisible(visible) {
    this.showLevels = visible;
    this.levelLayer?.setVisible(visible);
    this.#renderLevels();
  }

  setGridVisible(visible) {
    const color = visible ? THEME.grid : 'transparent';
    const options = { grid: { vertLines: { color }, horzLines: { color } } };
    this.chart.applyOptions(options);
    for (const pane of this.panes.values()) pane.chart.applyOptions(options);
  }

  /** Ascunde axele de preț și timp — mai mult loc pentru lumânări. */
  setAxesVisible(visible) {
    this.chart.applyOptions({
      rightPriceScale: { visible },
      timeScale: { visible },
    });
    for (const pane of this.panes.values()) {
      pane.chart.applyOptions({ rightPriceScale: { visible } });
    }
  }

  // -------------------------------------------------------------- panouri

  #syncPanes() {
    const wanted = PANE_DEFS.filter((p) => this.indicators[p.id]);
    const wantedIds = new Set(wanted.map((p) => p.id));

    for (const [id, pane] of this.panes) {
      if (!wantedIds.has(id)) {
        pane.chart.remove();
        pane.element.remove();
        this.panes.delete(id);
      }
    }

    for (const def of wanted) {
      if (this.panes.has(def.id)) continue;
      this.panes.set(def.id, this.#createPane(def));
    }

    // Reordonăm după PANE_DEFS ca panourile să nu sară la fiecare comutare.
    for (const def of PANE_DEFS) {
      const pane = this.panes.get(def.id);
      if (pane) this.panesEl.append(pane.element);
    }
    this.resize();
  }

  #createPane(def) {
    const element = el('div', { class: 'chart-pane chart-pane-sub' }, [
      el('span', { class: 'chart-pane-label', text: def.label }),
    ]);
    this.panesEl.append(element);

    const options = BASE_OPTIONS();
    options.timeScale.visible = false;
    options.rightPriceScale.scaleMargins = { top: 0.15, bottom: 0.1 };
    options.handleScale.axisPressedMouseMove = false;
    const chart = LWC().createChart(element, options);

    const series = {};
    if (def.id === 'rsi') {
      series.line = chart.addLineSeries({ color: '#b07cf0', lineWidth: 1, priceLineVisible: false });
      for (const [level, color] of [[70, 'rgba(239,83,80,0.4)'], [30, 'rgba(38,166,154,0.4)']]) {
        series.line.createPriceLine({ price: level, color, lineWidth: 1, lineStyle: 2, axisLabelVisible: false });
      }
    } else if (def.id === 'macd') {
      series.histogram = chart.addHistogramSeries({ priceLineVisible: false, lastValueVisible: false });
      series.macd = chart.addLineSeries({ color: '#2f81f7', lineWidth: 1, priceLineVisible: false });
      series.signal = chart.addLineSeries({ color: '#f0b90b', lineWidth: 1, priceLineVisible: false });
    } else if (def.id === 'ao') {
      series.histogram = chart.addHistogramSeries({ priceLineVisible: false, lastValueVisible: false });
    } else if (def.id === 'cvd') {
      series.line = chart.addLineSeries({ color: '#4dd0e1', lineWidth: 1, priceLineVisible: false });
    } else if (def.id === 'oichart') {
      series.line = chart.addAreaSeries({
        lineColor: '#f0b90b', topColor: 'rgba(240,185,11,0.25)', bottomColor: 'rgba(240,185,11,0)',
        lineWidth: 1, priceLineVisible: false,
      });
    }

    // Sincronizare bidirecțională a scării de timp cu panoul principal.
    let syncing = false;
    const link = (from, to) => from.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (syncing || !range) return;
      syncing = true;
      to.timeScale().setVisibleLogicalRange(range);
      syncing = false;
    });
    const unlinkMain = link(this.chart, chart);
    const unlinkSub = link(chart, this.chart);

    return { ...def, element, chart, series, unlink: [unlinkMain, unlinkSub] };
  }

  #renderPanes({ live = false } = {}) {
    if (!this.bars.length) return;

    const rsiPane = this.panes.get('rsi');
    if (rsiPane) rsiPane.series.line.setData(ind.rsi(this.bars, 14));

    const macdPane = this.panes.get('macd');
    if (macdPane) {
      const m = ind.macd(this.bars);
      macdPane.series.histogram.setData(m.histogram);
      macdPane.series.macd.setData(m.macd);
      macdPane.series.signal.setData(m.signal);
    }

    const aoPane = this.panes.get('ao');
    if (aoPane) aoPane.series.histogram.setData(ind.awesome(this.bars));

    const cvdPane = this.panes.get('cvd');
    if (cvdPane && !live) {
      const series = ind.cvd(this.bars);
      cvdPane.series.line.setData(series);
      this.cvdRunning = series.length ? series[series.length - 1].value : 0;
    }

    const oiPane = this.panes.get('oichart');
    if (oiPane && !live) this.#loadOpenInterest(oiPane);
  }

  async #loadOpenInterest(pane) {
    if (this.market !== 'futures') { pane.series.line.setData([]); return; }
    try {
      const resp = await fetch(`/api/oi-history?symbol=${encodeURIComponent(this.symbol)}&tf=${this.tf}`);
      if (!resp.ok) return;
      const data = await resp.json();
      if (this.destroyed) return;
      pane.series.line.setData(
        (data.points || []).map(([ts, value]) => ({ time: Math.floor(ts / 1000), value })),
      );
    } catch { /* OI e opțional */ }
  }

  // ------------------------------------------------------------- comenzi

  setIndicators(indicators) {
    this.indicators = { ...this.indicators, ...indicators };
    this.#renderVolume();
    this.#renderOverlays();
    this.#syncPanes();
    this.#renderPanes();
    this.#subscribeLive();
  }

  async setSymbol(symbol, { exchange, market } = {}) {
    this.symbol = symbol;
    if (exchange) this.exchange = exchange;
    if (market) this.market = market;
    this.#clearSubs();
    this.cvdRunning = 0;
    // Vederea veche nu are sens pe altă monedă: golim datele înainte de a le
    // înlocui, ca scara să nu încerce să acopere ambele intervale de preț.
    this.bars = [];
    this.candles.setData([]);
    this.volume.setData([]);
    this.levelLayer?.setLevels([]);
    this.measure?.cancel();
    await this.load();
  }

  async setTimeframe(tf) {
    if (tf === this.tf) return;
    this.tf = tf;
    this.#clearSubs();
    // Golim barele înainte să le cerem pe cele noi, ca la schimbarea monedei.
    //
    // Fără asta, între rândul de sus şi sfârşitul încărcării — o cerere la reţea, adică
    // o jumătate de secundă pe telefon — `tf` era deja cel nou, iar `bars` erau încă
    // cele vechi. `logicalForTime` amesteca atunci pasul noului taimfreim cu barele
    // celui vechi: un capăt de linie se aşeza după căutarea binară în barele vechi,
    // celălalt se extrapola cu pasul nou, şi desenele se lipeau aiurea, întinse peste
    // tot graficul. Se îndrepta singur după încărcare, deci era greu de prins.
    this.bars = [];
    this.candles.setData([]);
    this.volume.setData([]);
    this.levelLayer?.setLevels([]);
    this.measure?.cancel();
    await this.load();
  }

  // ------------------------------------------------- coordonate ↔ piață
  //
  // `timeToCoordinate` și `coordinateToTime` întorc `null` în afara intervalului de
  // lumânări — inclusiv în zona goală din dreapta ultimei bare, unde utilizatorul chiar
  // vrea să deseneze. Indicele logic e continuu, deci trecem prin el și extrapolăm cu
  // pasul timeframe-ului dincolo de capete.

  /**
   * Cât ţine o lumânare, după barele care chiar sunt pe ecran.
   *
   * `TF_SECONDS[this.tf]` spune cât *ar trebui* să ţină. Când cele două nu se potrivesc
   * — o clipă de la schimbarea taimfreimului, un răspuns întârziat — extrapolarea şi
   * căutarea binară lucrează pe scări diferite, iar desenele sar. Barele nu mint.
   */
  get pasBare() {
    const bars = this.bars;
    if (bars.length >= 2) {
      const ultim = bars[bars.length - 1].time - bars[bars.length - 2].time;
      if (ultim > 0) return ultim;
    }
    return TF_SECONDS[this.tf] || 300;
  }

  logicalForTime(time) {
    const bars = this.bars;
    if (!bars.length) return null;
    const step = this.pasBare;
    const last = bars.length - 1;
    if (time <= bars[0].time) return (time - bars[0].time) / step;
    if (time >= bars[last].time) return last + (time - bars[last].time) / step;

    let lo = 0;
    let hi = last;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (bars[mid].time <= time) lo = mid; else hi = mid;
    }
    const span = bars[hi].time - bars[lo].time || step;
    return lo + (time - bars[lo].time) / span;
  }

  timeForLogical(logical) {
    const bars = this.bars;
    if (!bars.length) return null;
    const step = this.pasBare;
    const last = bars.length - 1;
    if (logical <= 0) return Math.round(bars[0].time + logical * step);
    if (logical >= last) return Math.round(bars[last].time + (logical - last) * step);
    const i = Math.floor(logical);
    const next = bars[i + 1]?.time ?? bars[i].time + step;
    return Math.round(bars[i].time + (logical - i) * (next - bars[i].time));
  }

  /** Punct de piață (timp + preț) -> pixeli, valid și dincolo de ultima lumânare. */
  toPixel(point) {
    const timeScale = this.chart.timeScale();
    let x = timeScale.timeToCoordinate(point.time);
    if (x === null) {
      const logical = this.logicalForTime(point.time);
      x = logical === null ? null : timeScale.logicalToCoordinate(logical);
    }
    const y = this.candles.priceToCoordinate(point.price);
    return x === null || y === null ? null : { x, y };
  }

  /** Pixeli -> punct de piață, valid și în zona goală din dreapta. */
  toMarket(x, y) {
    const timeScale = this.chart.timeScale();
    let time = timeScale.coordinateToTime(x);
    if (time === null) {
      const logical = timeScale.coordinateToLogical(x);
      time = logical === null ? null : this.timeForLogical(logical);
    }
    const price = this.candles.coordinateToPrice(y);
    if (time === null || price === null) return null;
    return { time, price };
  }

  /** Metricile pentru bara „технические данные". */
  tech() {
    return ind.techData(this.bars);
  }

  /**
   * Readuce graficul la vederea implicită.
   *
   * Trebuie apelat la fiecare schimbare de monedă sau timeframe. Fără el, scara de preț
   * rămâne cea a monedei anterioare: dacă utilizatorul a tras de axă, Lightweight Charts
   * dezactivează definitiv `autoScale`, iar o monedă la 17$ ajunge desenată pe o axă de
   * 70$ — lumânările se strâng într-o dungă la marginea de jos.
   */
  resetView() {
    const visible = this.compact ? 120 : 220;
    const rightOffset = this.compact ? 2 : 4;

    this.chart.priceScale('right').applyOptions({ autoScale: true });
    for (const pane of this.panes.values()) {
      pane.chart.priceScale('right').applyOptions({ autoScale: true });
    }

    const timeScale = this.chart.timeScale();
    timeScale.applyOptions({ rightOffset });

    const n = this.bars.length;
    if (n > visible) {
      timeScale.setVisibleLogicalRange({ from: n - visible, to: n + rightOffset });
    } else {
      timeScale.fitContent();
    }

    // Straturile proprii desenează în coordonate de piață — trebuie redesenate
    // după ce scara s-a schimbat.
    this.levelLayer?.render();
    this.measure?.render();
  }

  fitContent() {
    this.chart.timeScale().fitContent();
  }

  scrollToRealtime() {
    this.chart.timeScale().scrollToRealTime();
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    this.#clearSubs();
    this.historyUnsub?.();
    this.offTheme?.();
    if (this.viewFrame) cancelAnimationFrame(this.viewFrame);
    this.measure?.destroy();
    this.levelLayer?.destroy();
    for (const layer of this.extraLayers || []) layer.destroy?.();
    this.resizeObserver?.disconnect();
    for (const pane of this.panes.values()) {
      pane.unlink?.forEach((fn) => fn?.());
      pane.chart.remove();
    }
    this.panes.clear();
    this.chart.remove();
    this.root.remove();
  }
}
